<template>
<b-container>
  <b-container v-if="!page_show">
      waiting...
  </b-container>
  <b-container v-if="page_show">
    <!-- File validation error -->
    <b-modal style="z-index:-1" centered ref="fileValidationModal" id="file_validation_error" title="File Validation Error" >
        <p class="my-4">
            The uploaded file does not meet the required format or size limits.
            <br>Please check that your file is a valid FASTA format and under 20KB.
            <br>Refer to the [About] page for format specifications.
        </p>
    </b-modal>

    <!-- MSA queue busy error -->
    <b-modal style="z-index:-1" centered ref="msaQueueModal" id="msa_queue_busy" title="MSA Service Busy" >
        <p class="my-4">
            <strong>The MSA generation service is currently processing another job.</strong>
            <br><br>Up to 5 MSA generation jobs can run concurrently due to computational resource limits.
            <br><br><strong>Alternative options:</strong>
            <br>• Try again in a few minutes
            <br>• Use "Single sequence inference" for faster results
            <br>• Use "Provide MSA" if you have your own alignment file
        </p>
    </b-modal>

    <!-- Invalid file type error -->
    <b-modal style="z-index:-1" centered ref="fileTypeModal" id="invalid_file_type" title="Invalid File Type" >
        <p class="my-4">
            The uploaded file type is not supported.
            <br>Please upload a valid FASTA (.fasta or .fa) file.
            <br>Refer to the examples provided for proper file format.
        </p>
    </b-modal>

    <!-- General error -->
    <b-modal style="z-index:-1" centered ref="generalErrorModal" id="general_error" title="Submission Failed" >
        <p class="my-4">
            An unexpected error occurred during submission.
            <br>Please try again later or contact support if the problem persists.
        </p>
    </b-modal>
    
    <b-row>
      <text-header msg="Introduction"></text-header>
    <hr>
      <p class="intro-text">RhoFold+ is an accurate RNA three-dimensional structure prediction with end-to-end advanced deep neural networks and large RNA language models. Several novel components, such as a fully-differentiable end-to-end pipeline, are proposed to overcome the challenge of data scarcity and force the model to learn the folding principles implicitly. A comprehensive evaluation using RNA-Puzzles structures and all the existing experimental RNA structures demonstrates the effectiveness of our proposed method. RhoFold+ can also correct crystallization artifacts by folding the biologically meaningful structures correctly for the swapped dimers.
Further cross-family and cross-type validations demonstrate RhoFold+'s generalizability to predict structures from potential unseen RNA families and types. Extensive computation experiments demonstrate that RhoFold+ learns the RNA folding principle instead of only remembering the database such as predicting biologically meaningful structures correctly for swapped dimers and thus has strong generalization properties and broad usage.
Moreover, the computationally-predicted structures from RhoFold+ can facilitate construct engineering and potentially guide experimental methods to resolve more high-resolution RNA structures using oligomerization.
      </p>
    </b-row>
    
    <b-row>
      <b-form @submit="onSearchUniqueId()">
        <p class="section-text">To check the status of a job, please input the job ID:</p>
        <b-form-input type="text" v-model="unique_id"></b-form-input>
        <div class="button-group" style="margin-top:10px">
          <b-button class="unified-button" style="margin-right:10px" variant="outline-primary" type="submit">Search</b-button>
          <b-button class="unified-button" variant="outline-primary" @click="unique_id = null">Clear</b-button> 
        </div>
      </b-form>
      <b-table style="margin:10px" v-show=unique_id_searched striped hover id="my-table" :items="items" :fields="fields"
                per-page="1" current-page="1">
                <template #cell(download)=row>
                    <b-button class="unified-button" :href="`/aihlab/RhoFold+/#/predict/${unique_id}`" variant="outline-primary">
                    Check Status >>
                    </b-button>
                </template>
                <template #cell(unique_id)>
                    {{unique_id}}
                </template>
            </b-table>
    </b-row>

    <b-row>
        <text-header msg="RNA 3D Structure Prediction"></text-header>
        <hr>

        <p class='note-text'>NOTE: {{process_running}} job(s) currently in queue.</p>
        
        <!-- News section - temporarily hidden -->
        <!-- 
        <div class="news-section">
        <p class="section-text">News: we are now supporting double-strand RNA 3D structure prediction, input file with sequence being separated by a ";". example:<br>
          <pre>>5y87_C_D
ACCCGCAAGGCCGACGGC;GCCGCCGCUGGUGCAAGUCCAGCCACGCUUCGGCGUGGGCGCUCAUGGGU
          </pre>
          <b-row style="margin-top:10px">
              <b-col style="margin-top:15px" cols="4">
                <p class="section-text"><strong>Example double-strand RNA sequence file in .fasta format:</strong></p>
              </b-col> 
              <b-col cols="8" class="download-col">
                <b-button class="unified-button" variant="outline-primary" @click="downloadfile('5y87_C_D.fasta')">Download</b-button>
              </b-col>
          </b-row>
        </p>
        </div>
        -->

        <!-- Step 1: Choose Inference Type -->
        <div class="step-section">
          <h4 class="step-title">Step 1: Choose Inference Type</h4>
          <b-form-group label-for="inference-select">
            <b-form-select 
              id="inference-select" 
              v-model="selected" 
              :options="inferenceOptions"
              size="lg"
              class="inference-dropdown"
            ></b-form-select>
          </b-form-group>

          <!-- MSA Generation Notice -->
          <div class="warning-notice" v-if="selected=='generate_msa'">
            <p class="section-text"><strong>⚠️ MSA Generation Notice:</strong></p>
            <p class="section-text">The Multiple Sequence Alignment (MSA) generation process requires significant computational resources and may take several hours to complete. Due to resource limitations, up to <strong>5 MSA generation jobs</strong> can be processed concurrently.</p>
            <p class="section-text"><strong>Current Status:</strong> {{process_running}} job(s) currently in queue</p>
            <p v-if="parseInt(process_running) > 0" class="section-text queue-warning"><strong>⏳ Queue Status:</strong> Other jobs are currently running. Your submission may be delayed.</p>
            <p class="section-text"><strong>Alternatives for faster results:</strong><br>
            • Use "Single sequence inference" (2-10 minutes)<br>
            • Use "Provide MSA" if you have your own alignment file</p>
          </div>

          <!-- Single Sequence Testing Notice -->
          <p class='warning-notice' v-if="selected=='single_seq'">This function is in testing. It's not as accurate as the MSA version. It's only for the user to generate a quick reference structure.</p>
        </div>

        <!-- Step 2: FASTA Input -->
        <div class="step-section" v-if="selected">
          <h4 class="step-title">Step 2: Input RNA Sequence (FASTA format)</h4>
          
          <!-- Input Method Selection for Single Sequence -->
          <div v-if="selected=='single_seq'">
            <p class="section-text">Choose your input method:</p>
            <div class="input-method-buttons">
              <b-button 
                :variant="singleSeqInputMethod == 'manual' ? 'primary' : 'outline-primary'"
                @click="singleSeqInputMethod = 'manual'"
                class="method-button"
              >
                Copy & Paste FASTA
              </b-button>
              <b-button 
                :variant="singleSeqInputMethod == 'file' ? 'primary' : 'outline-primary'"
                @click="singleSeqInputMethod = 'file'"
                class="method-button"
              >
                Upload FASTA File
              </b-button>
            </div>
          </div>

          <!-- Manual Input -->
          <div v-if="(selected=='single_seq' && singleSeqInputMethod == 'manual')">
            <p class="section-text">Enter FASTA content manually (10-500 characters):</p>
            <b-form-textarea
                placeholder="Please enter at least 10, but no more than 500 characters&#10;eg.&#10;>3meiA&#10;CCGCCGCGCCAUGCCUGUGGCGG"
                rows="6"
                max-rows="10"
                v-model="fastaValue"
                :state="fastaValue.length >= 10 && fastaValue.length < 500"
                class="fasta-textarea"
            ></b-form-textarea>
            <p class="note-text">The single-sequence inference process generally takes 2 to 10 minutes.</p>
          </div>

          <!-- File Upload for FASTA -->
          <div v-if="(selected=='single_seq' && singleSeqInputMethod == 'file') || selected=='provide_msa' || selected=='generate_msa'">
            <p class="section-text">Upload your FASTA sequence file:</p>
            <div class="upload-area" 
                 @drop="handleDrop" 
                 @dragover.prevent 
                 @dragenter.prevent
                 :class="{ 'drag-over': isDragOver }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false"
                 @drop="isDragOver = false"
                 @click="triggerFileInput('file_embedding')">
              <input 
                type="file" 
                ref="fastaFileInput"
                @change="onFileChange"
                accept=".fasta,.fa"
                style="display: none;"
              />
              <div class="upload-content">
                <i class="upload-icon">📁</i>
                <p class="upload-text">Drop your FASTA file here or <span class="upload-link">click to select</span></p>
                <p class="file-info" v-if="fileAll">Selected: {{ fileAll.name }}</p>
              </div>
            </div>

            <b-row class="example-row">
              <b-col cols="6" class="example-text-col">
                <p class="section-text"><strong>Example sequence file in .fasta format:</strong></p>
              </b-col>
              <b-col cols="6" class="download-col">
                <b-button class="unified-button" variant="outline-primary" @click="downloadfile('3SUH.fasta')">Download Example</b-button>
              </b-col>
            </b-row>
          </div>
        </div>

        <!-- Step 3: MSA File (Optional) -->
        <div class="step-section" v-if="selected=='provide_msa'">
          <h4 class="step-title">Step 3: Upload MSA File (.a3m format)</h4>
          <p class="section-text">Upload your Multiple Sequence Alignment file:</p>
          <div class="upload-area" 
               @drop="handleMSADrop" 
               @dragover.prevent 
               @dragenter.prevent
               :class="{ 'drag-over': isMSADragOver }"
               @dragenter="isMSADragOver = true"
               @dragleave="isMSADragOver = false"
               @drop="isMSADragOver = false"
               @click="triggerFileInput('file_msa')">
            <input 
              type="file" 
              ref="msaFileInput"
              @change="onMSAFileChange"
              accept=".a3m"
              style="display: none;"
            />
            <div class="upload-content">
              <i class="upload-icon">📁</i>
              <p class="upload-text">Drop your .a3m file here or <span class="upload-link">click to select</span></p>
              <p class="file-info" v-if="fileMSA">Selected: {{ fileMSA.name }}</p>
            </div>
          </div>

          <b-row class="example-row">
            <b-col cols="6" class="example-text-col">
              <p class="section-text"><strong>Example MSA result file in .a3m format:</strong></p>
            </b-col>
            <b-col cols="6" class="download-col">
              <b-button class="unified-button" variant="outline-primary" @click="downloadfile('3SUH.a3m')">Download Example</b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Final Step: Email (Optional) -->
        <div class="step-section" v-if="selected">
          <h4 class="step-title">Final Step: Email Notification (Optional)</h4>
          <p class="section-text">Enter your email address to receive status update notifications:</p>
          <p class="note-text">If you don't receive the email, please check your spam folder.</p> 
          <b-form-input 
            type='email' 
            v-model="user_email" 
            placeholder="your.email@example.com"
            class="email-input"
          ></b-form-input>
        </div>

        <!-- Submit Section -->
        <div v-if="selected">
          <b-form @submit="selected=='single_seq' && singleSeqInputMethod == 'manual' ? onSubmitAll('predict', 'text') : onSubmitAll('predict', 'file')">
            <div class="button-group" style="margin-top:20px">
              <b-button class="unified-button" style="margin-right:10px" variant="outline-primary" type="submit">Submit Prediction</b-button>
              <b-button class="unified-button reset-button" variant="outline-secondary" @click="resetForm()">Reset Form</b-button> 
            </div>
          </b-form>
        </div>
    </b-row>

    <!-- Citation Section -->
    <b-row>
        <text-header msg="Citation"></text-header>
        <hr>
        <p class="section-text">
          If you find RhoFold+ useful in your research, please cite our paper published in <strong>Nature Methods</strong>. 
          For more detailed information about the methodology, performance evaluation, and technical details, 
          please refer to our publication.
        </p>
        
        <div class="github-code-block">
          <div class="code-block-header">
            <div class="language-badge">
              <span class="language-text">bibtex</span>
            </div>
            <b-button 
              class="copy-button" 
              variant="link" 
              size="sm"
              @click="copyCitation"
              :title="copyButtonText"
            >
              <svg class="copy-icon" viewBox="0 0 16 16" width="14" height="14">
                <path fill="currentColor" d="M5.75 1a.75.75 0 0 0-.75.75v3c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-3a.75.75 0 0 0-.75-.75h-4.5zm.75 3V2.5h3V4h-3zm-2.874-.467a.75.75 0 0 0-.752-1.298A1.75 1.75 0 0 0 1.25 3.75v9.5c0 .966.784 1.75 1.75 1.75h8.5a1.75 1.75 0 0 0 1.75-1.75v-2.5a.75.75 0 0 0-1.5 0v2.5a.25.25 0 0 1-.25.25h-8.5a.25.25 0 0 1-.25-.25v-9.5a.25.25 0 0 1 .25-.25c.085 0 .166.018.24.05z"></path>
              </svg>
              {{ copyButtonText }}
            </b-button>
          </div>
          <div class="code-content">
            <pre class="citation-code" ref="citationText">@article{shen2024accurate,
  title={Accurate RNA 3D structure prediction using a language model-based deep learning approach},
  author={Shen, Tao and Hu, Zhihang and Sun, Siqi and Liu, Di and Wong, Felix and Wang, Jiuming and Chen, Jiayang and Wang, Yixuan and Hong, Liang and Xiao, Jin and others},
  journal={Nature Methods},
  volume={21},
  number={12},
  pages={2287--2298},
  year={2024},
  publisher={Nature Publishing Group US New York}
}</pre>
          </div>
        </div>
        
        <p class="section-text">
          <strong>Paper Link:</strong> 
          <a href="https://www.nature.com/articles/s41592-024-02487-0" target="_blank" class="paper-link">
            Nature Methods - Accurate RNA 3D structure prediction using a language model-based deep learning approach
          </a>
        </p>
    </b-row>

  </b-container>
</b-container>
</template>

<script>

</script>

<script>
import TextHeader from './TextHeader.vue'
import myJson from '../json/data.json'
import FileSaver from 'file-saver';


export default {
  name: 'Introduction',
  components: {TextHeader},
  data () {
      return {
        myJson,
        unique_id_searched: false,
        unique_id: '',
        user_email: '',
        fileAll: null,
        fileMSA: null,
        selected: '',
        singleSeqInputMethod: 'manual',
        isDragOver: false,
        isMSADragOver: false,
        copyButtonText: 'Copy',
        citationText: `@article{shen2024accurate,
  title={Accurate RNA 3D structure prediction using a language model-based deep learning approach},
  author={Shen, Tao and Hu, Zhihang and Sun, Siqi and Liu, Di and Wong, Felix and Wang, Jiuming and Chen, Jiayang and Wang, Yixuan and Hong, Liang and Xiao, Jin and others},
  journal={Nature Methods},
  volume={21},
  number={12},
  pages={2287--2298},
  year={2024},
  publisher={Nature Publishing Group US New York}
}`,
        inferenceOptions: [
          {
            text: 'Select inference type...',
            value: '',
            disabled: true
          },
          {
            text: 'Single sequence inference - Fast prediction using single sequence (2~5 minutes)',
            value: 'single_seq'
          },
          {
            text: 'Provide MSA - Use your own MSA file with sequence (2~5 minutes)',
            value: 'provide_msa'
          },
          {
            text: 'Generate MSA - Server generates MSA from sequence (several hours)',
            value: 'generate_msa'
          }
        ],
        singleSeqInputOptions: [
          {
            text: 'Copy and paste FASTA content',
            value: 'manual'
          },
          {
            text: 'Upload FASTA file from computer',
            value: 'file'
          }
        ],
        fastaValue: ">3meiA\nCCGCCGCGCCAUGCCUGUGGCGG",
        page_show: true,
        process_running:'0',
        items: [],
        download_string: 'No Such File',
        fields: [
            {
                key: 'unique_id',
                label: 'Unique Job ID'
            },
            {
                key: 'download',
                label: 'Status',
            },
        ],
      }
  },
  created() {
    this.items = [
                {
                    unique_id: this.unique_id,
                    download: 'Download'
                }
            ]
  },
  mounted() {
     let submit_url = `${this.baseURL}/running_jobs`
     this.axios.get(submit_url,
     { 
          headers: {
            'Content-Type': 'multipart/form-data'
          },
        })
        .then((response) => {
          this.process_running = response.data
          })
        .catch((error) => {
          console.log(error);
        });
  },
  methods: {
    copyCitation() {
      navigator.clipboard.writeText(this.citationText).then(() => {
        this.copyButtonText = 'Copied!';
        setTimeout(() => {
          this.copyButtonText = 'Copy';
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy citation: ', err);
        // Fallback for older browsers
        this.selectText();
      });
    },
    selectText() {
      if (this.$refs.citationText) {
        const range = document.createRange();
        range.selectNode(this.$refs.citationText);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        this.copyButtonText = 'Selected';
        setTimeout(() => {
          this.copyButtonText = 'Copy';
        }, 2000);
      }
    },
    triggerFileInput(inputType) {
      if (inputType === 'file_embedding') {
        this.$refs.fastaFileInput.click();
      } else if (inputType === 'file_msa') {
        this.$refs.msaFileInput.click();
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.fileAll = file;
      }
    },
    onMSAFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.fileMSA = file;
      }
    },
    handleDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.fileAll = files[0];
      }
    },
    handleMSADrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.fileMSA = files[0];
      }
    },
    resetForm() {
      this.fileAll = null;
      this.fileMSA = null;
      this.fastaValue = ">3meiA\nCCGCCGCGCCAUGCCUGUGGCGG";
      this.user_email = '';
      this.singleSeqInputMethod = 'manual';
      if (this.$refs.fastaFileInput) {
        this.$refs.fastaFileInput.value = '';
      }
      if (this.$refs.msaFileInput) {
        this.$refs.msaFileInput.value = '';
      }
    },
    showErrorModal(errorCode) {
      console.log('Showing error modal for:', errorCode);
      this.$nextTick(() => {
        switch(errorCode) {
          case 'ERROR_FILE_VALIDATION':
            if (this.$refs.fileValidationModal) {
              this.$refs.fileValidationModal.show();
            } else {
              console.error('fileValidationModal ref not found');
              alert('File validation error: Please check your file format and size.');
            }
            break;
          case 'ERROR_MSA_QUEUE_BUSY':
            if (this.$refs.msaQueueModal) {
              this.$refs.msaQueueModal.show();
            } else {
              console.error('msaQueueModal ref not found');
              alert('MSA service is busy. Please try again later or use single sequence inference.');
            }
            break;
          case 'ERROR_INVALID_FILE_TYPE':
            if (this.$refs.fileTypeModal) {
              this.$refs.fileTypeModal.show();
            } else {
              console.error('fileTypeModal ref not found');
              alert('Invalid file type. Please upload a FASTA file.');
            }
            break;
          default:
            if (this.$refs.generalErrorModal) {
              this.$refs.generalErrorModal.show();
            } else {
              console.error('generalErrorModal ref not found, errorCode:', errorCode);
              alert('An error occurred: ' + errorCode);
            }
        }
      });
    },
    downloadfile(file) {
        const finame = `${this.baseURL}/download?filename=${file}`;
        FileSaver.saveAs(finame, file);
    },
    onSearchUniqueId() {
        let submit_url = `${this.baseURL}/predict`
        this.unique_id_searched = true;
        this.axios({
            method: 'get',
            url: submit_url,
            params: {
                unique_id: this.unique_id
            }
        })
        .then((response) => {
          if (response.data == 'Fail') {
            this.download_string = "No Such File"
          }else if (response.data == 'Queue') {
            this.download_string = "In Progress"
          }else {
            this.download_string = "Download";
            this.$router.push({ name: 'predict', params: { query: this.unique_id, unique_id: this.unique_id} });
          }
        })
        .catch((error) => {
            console.log(error);
        });
    },
    onSubmitAll(field, file_text) {
        let upfile;
        let upfile_msa;
        let submit_url = `${this.baseURL}/${field}`
        let date = new Date();
        date = date.getTime();
        let filename = date+'.fasta'
        let filename_msa = ''
        this.page_show = false;
        
        if (file_text == 'file') {
          upfile = this.fileAll;
          filename = upfile.name;
          if ( this.selected == 'provide_msa') {
            upfile_msa = this.fileMSA;
            filename_msa = upfile_msa.name;
          }
        } else if (file_text == 'text') {
          upfile = new Blob([this.fastaValue], {type : 'application/octet-stream'});
          filename = date+'.fasta';
          this.selected = 'single_seq';
        }
        console.log("submit_url: ",submit_url);
        var formData = new FormData();
        formData.append(`${field}_file`, upfile);
        if ( this.selected == 'provide_msa') {
          formData.append(`${field}_file_msa`, upfile_msa);
        }
        this.axios.post(submit_url,
        formData,
        { 
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          params: this.user_email ? {
            "date_id": date,
            "field": file_text,
            "style": this.selected,
            'user_email':this.user_email
          } : {
            "date_id": date,
            "field": file_text,
            "style": this.selected
          },
        })
        .then((response) => {
          if (response.data.includes('ERROR_'))  {
            this.page_show = true;
            this.showErrorModal(response.data);
            return;
          }
          this.unique_id = response.data
          console.log(response)
          this.$router.push({ name: 'predict', params: { query: filename, unique_id: this.unique_id} });
        })
        .catch((error) => {
          console.log(error);
          this.page_show = true;
          this.showErrorModal('ERROR_GENERAL');
        });
    }
  }
}

</script>

<style scoped>
/* Unified Typography */
.intro-text {
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.section-text {
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 10px;
}

.note-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  padding: 10px 0;
  border-bottom: 2px solid #e9ecef;
}

/* Step Sections */
.step-section {
  margin: 30px 0;
  padding: 20px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background-color: #f8f9fa;
}

/* GitHub-style Code Block for Citation */
.github-code-block {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin: 20px 0;
  position: relative;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
  overflow: hidden;
}

.code-block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8f9fa;
  border-bottom: 0px solid #e9ecef;
  padding: 8px 16px;
  font-size: 12px;
}

.language-badge {
  display: flex;
  align-items: center;
}

.language-text {
  color: #7d8590;
  font-weight: 500;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
}

.copy-button {
  color: black;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1;
  background: none;
  border: 1px solid #e9ecef;
  border-radius: 3px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.copy-button:hover {
  color: white;
  background-color: #6c757d;
  border-color: #6c757d;
  text-decoration: none;
}

.copy-button:focus {
  outline: 2px solid #0969da;
  outline-offset: -2px;
}

.copy-icon {
  width: 14px;
  height: 14px;
  opacity: 0.8;
}

.code-content {
  background-color: #f8f9fa;
  padding: 16px;
}

.citation-code {
  color: black;
  background: transparent;
  border: none;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 14px;
  line-height: 1.45;
  margin: 0;
  white-space: pre;
  word-wrap: normal;
  overflow-x: auto;
  tab-size: 4;
  user-select: all;
}

/* Syntax highlighting for BibTeX-like appearance */
.citation-code::before {
  content: '';
}

.paper-link {
  color: #007bff;
  text-decoration: none;
}

.paper-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* Unified Button Styling */
.unified-button {
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #007bff;
  transition: all 0.2s ease;
  min-width: 120px;
}

.unified-button:hover {
  background-color: #007bff;
  color: white;
}

.reset-button {
  border-color: #6c757d !important;
  color: #6c757d;
}

.reset-button:hover {
  background-color: #6c757d !important;
  color: white;
}

/* Button Groups */
.button-group {
  text-align: left;
}

.input-method-buttons {
  margin: 20px 0;
  display: flex;
  gap: 15px;
}

.method-button {
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
  border-radius: 4px;
  transition: all 0.2s ease;
  min-width: 180px;
}

/* Layout Improvements */
.example-row {
  margin-top: 15px;
  align-items: center;
}

.example-text-col {
  display: flex;
  align-items: center;
}

.download-col {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

/* Upload Area Styling */
.upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  transition: all 0.3s ease;
  margin: 15px 0;
  background-color: white;
  position: relative;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.upload-area.drag-over {
  border-color: #007bff;
  background-color: #e3f2fd;
}

.upload-area:hover {
  border-color: #007bff;
  background-color: #f0f8ff;
}

.upload-content {
  width: 100%;
  pointer-events: none;
}

.upload-icon {
  font-size: 24px;
  margin-bottom: 10px;
  display: block;
}

.upload-text {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.upload-link {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  pointer-events: auto;
}

.upload-link:hover {
  color: #0056b3;
}

.file-info {
  font-size: 14px;
  color: #28a745;
  margin-top: 10px;
  font-weight: 600;
}

/* Form Elements */
.inference-dropdown {
  font-size: 16px;
  margin-bottom: 20px;
}

.input-method-radio {
  margin: 15px 0;
}

.fasta-textarea {
  font-size: 14px;
  font-family: 'Courier New', monospace;
  margin: 15px 0;
}

.email-input {
  font-size: 16px;
  margin: 10px 0;
  max-width: 400px;
}

/* Warning and Notice Styling */
.warning-notice {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  padding: 15px;
  margin: 15px 0;
}

.queue-warning {
  color: #d63384 !important;
  font-weight: 600;
}

/* Legacy styles for compatibility */
a {
  color: #3a8ccf;
  text-decoration: none;
}

.mb-2 {
    width: 300px;
    margin: 4px;
    display: inline-block;
    vertical-align: top;
}

.warpper{
  display: grid;
  grid-template-columns: repeat(auto-fill, 300px);
  grid-gap: 40px;
  justify-content: center;
}

.md-card-actions{
  display: grid;
  justify-content: right;
}

.submit_bt {
  margin: 20px 0;
  text-align: center;
}
</style>
